import { queryOptions, useQuery } from '@tanstack/react-query';

import { axios } from '~/services/axios';

export const queryKeys = {
  galleryImage: (imageId: string, password?: string | null) =>
    ['gallery', imageId, password] as const,
  galleryFolder: (folderId: string, page: number, password?: string | null) =>
    ['gallery-folder', folderId, page, password] as const,
  galleryUser: (user: string, page: number, showFavorites: boolean) =>
    ['user-gallery', user, page, showFavorites] as const,
} as const;

export const fetchGalleryImage = async (
  imageId: string,
  password?: string | null
) => {
  try {
    const res = await axios.get(
      `/gallery/${imageId}${password ? `?password=${password}` : ''}`
    );

    return {
      image: res.data as GalleryImage,
      isProtected: res.data.password_protected,
      ok: true,
      status: res.status,
    };
  } catch (error: any) {
    if (error.response?.status === 403) {
      return {
        image: null,
        isProtected: true,
        ok: false,
        status: 403,
      };
    }
    throw error;
  }
};

export const galleryImageQuery = (imageId?: string, password?: string | null) =>
  queryOptions({
    queryKey: queryKeys.galleryImage(imageId ?? '', password),
    queryFn: () => fetchGalleryImage(imageId ?? '', password),
    enabled: !!imageId,
  });

export const useGalleryImage = (imageId?: string, password?: string | null) => {
  return useQuery(galleryImageQuery(imageId, password));
};

export const fetchGalleryFolder = async (
  folderId: string,
  page = 1,
  password?: string | null
) => {
  try {
    const res = await axios.get(`/gallery/folder/${folderId}`, {
      params: {
        page,
        password,
      },
    });

    return res.data as {
      data: GalleryImage[];
      meta: PaginationMeta;
    };
  } catch (error: any) {
    if (error.response?.status === 403) {
      return {
        data: [] as GalleryImage[],
        meta: {
          total_pages: 1,
        },
      };
    }
    throw error;
  }
};

export const galleryFolderQuery = (
  folderId?: string,
  page = 1,
  password?: string | null
) =>
  queryOptions({
    queryKey: queryKeys.galleryFolder(folderId ?? '', page, password),
    queryFn: () => fetchGalleryFolder(folderId ?? '', page, password),
    enabled: !!folderId,
  });

export const useGalleryFolder = (
  folderId?: string,
  page = 1,
  password?: string | null
) => {
  return useQuery(galleryFolderQuery(folderId, page, password));
};

export const fetchUserGallery = async (
  user?: string,
  page = 1,
  showFavorites = false
) => {
  const url = showFavorites
    ? `/gallery/favorites/${user}`
    : `/users/${user}/gallery`;

  const res = await axios.get<{
    data: GalleryImage[];
    meta: PaginationMeta;
  }>(url, {
    params: { page },
  });

  return res.data;
};

export const userGalleryQuery = (
  user?: string,
  page = 1,
  showFavorites = false
) =>
  queryOptions({
    queryKey: ['user-gallery', user, page, showFavorites],
    queryFn: () => fetchUserGallery(user, page, showFavorites),
    enabled: !!user,
  });

export const useUserGallery = (
  user?: string,
  page = 1,
  showFavorites = false
) => {
  return useQuery(userGalleryQuery(user, page, showFavorites));
};
